import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  rewriteListSchema,
  rewriteListsPageSchema,
  schainListSchema,
  schainListsPageSchema,
  type AllListsPage,
  type ListsFilter,
  type ListsPage,
  type RewriteList,
  type RewriteListMutationPayload,
  type SchainList,
  type SchainListMutationPayload,
} from './model';

interface ListsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const rewritePath = '/api/lists/rewrite';
const schainPath = '/api/lists/schain';
const emptyResponseSchema = z.preprocess((value) => (value === '' ? null : value), z.null());
const importResponseSchema = z.union([
  z.number(),
  z.string().trim().regex(/^\d+$/).transform(Number),
]);

export function createListsClient({ config, accessToken, fetcher }: ListsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  const listRewrite = (filter: ListsFilter) =>
    requestListPage(api, rewritePath, rewriteListsPageSchema, filter);
  const listSchain = (filter: ListsFilter) =>
    requestListPage(api, schainPath, schainListsPageSchema, filter);

  return {
    listRewrite,
    listAll: (filter: ListsFilter) => listCombinedPage(filter, listRewrite, listSchain),
    getRewrite: (id: string) =>
      api.request({ path: `${rewritePath}/${encodeURIComponent(id)}`, schema: rewriteListSchema }),
    createRewrite: (payload: RewriteListMutationPayload) =>
      api.request({
        path: rewritePath,
        schema: rewriteListSchema,
        init: jsonInit('POST', payload),
      }),
    updateRewrite: (id: string, payload: RewriteListMutationPayload) =>
      api.request({
        path: `${rewritePath}/${encodeURIComponent(id)}`,
        schema: rewriteListSchema,
        init: jsonInit('PUT', payload),
      }),
    deleteRewrite: (id: string) =>
      api.request({
        path: `${rewritePath}/${encodeURIComponent(id)}`,
        schema: emptyResponseSchema,
        init: { method: 'DELETE' },
      }),
    listSchain,
    getSchain: (id: string) =>
      api.request({ path: `${schainPath}/${encodeURIComponent(id)}`, schema: schainListSchema }),
    createSchain: (payload: SchainListMutationPayload) =>
      api.request({ path: schainPath, schema: schainListSchema, init: jsonInit('POST', payload) }),
    updateSchain: (id: string, payload: SchainListMutationPayload) =>
      api.request({
        path: `${schainPath}/${encodeURIComponent(id)}`,
        schema: schainListSchema,
        init: jsonInit('PUT', payload),
      }),
    deleteSchain: (id: string) =>
      api.request({
        path: `${schainPath}/${encodeURIComponent(id)}`,
        schema: emptyResponseSchema,
        init: { method: 'DELETE' },
      }),
    importSchain: (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return api.request({
        path: `${schainPath}/${encodeURIComponent(id)}/import`,
        schema: importResponseSchema,
        init: { method: 'POST', body },
      });
    },
    exportSchain: (id: string) =>
      api.requestBlob({ path: `${schainPath}/${encodeURIComponent(id)}/export` }),
  };
}

function requestListPage<TItem>(
  api: ReturnType<typeof createShigoApiClient>,
  path: string,
  schema: z.ZodType<{ content: TItem[]; totalElements: number }>,
  filter: ListsFilter,
): Promise<ListsPage<TItem>> {
  return api.request({
    path: `${path}?${toSearchParams({
      page: filter.page,
      size: filter.size,
      name: filter.searchTerm.trim() || undefined,
      sortProperty: 'updatedAt',
      sortDirection: 'DESC',
    })}`,
    schema,
  });
}

async function listCombinedPage(
  filter: ListsFilter,
  listRewrite: (filter: ListsFilter) => Promise<ListsPage<RewriteList>>,
  listSchain: (filter: ListsFilter) => Promise<ListsPage<SchainList>>,
): Promise<AllListsPage> {
  const schainFirstFilter = { ...filter, page: 0 };
  const [rewriteResult, schainResult] = await Promise.allSettled([
    listRewrite(filter),
    listSchain(schainFirstFilter),
  ]);
  if (rewriteResult.status === 'rejected' && schainResult.status === 'rejected') {
    throw rewriteResult.reason;
  }

  const rewritePage =
    rewriteResult.status === 'fulfilled' ? rewriteResult.value : { content: [], totalElements: 0 };
  const schainFirstPage =
    schainResult.status === 'fulfilled' ? schainResult.value : { content: [], totalElements: 0 };
  const start = filter.page * filter.size;
  const rewrite =
    start < rewritePage.totalElements ? rewritePage.content.slice(0, filter.size) : [];
  const schainStart = Math.max(0, start - rewritePage.totalElements);
  const schainCount = filter.size - rewrite.length;
  const schain =
    schainResult.status === 'fulfilled' && schainCount > 0
      ? await readSchainRange(schainStart, schainCount, filter, schainFirstPage, listSchain)
      : [];

  return {
    rewrite,
    schain,
    totalElements: rewritePage.totalElements + schainFirstPage.totalElements,
    partialFailure: rewriteResult.status === 'rejected' || schainResult.status === 'rejected',
  };
}

async function readSchainRange(
  start: number,
  count: number,
  filter: ListsFilter,
  firstPage: ListsPage<SchainList>,
  listSchain: (filter: ListsFilter) => Promise<ListsPage<SchainList>>,
) {
  const items: SchainList[] = [];
  let page = Math.floor(start / filter.size);
  let offset = start % filter.size;
  while (items.length < count && start + items.length < firstPage.totalElements) {
    const current = page === 0 ? firstPage : await listSchain({ ...filter, page });
    items.push(...current.content.slice(offset, offset + count - items.length));
    if (!current.content.length) break;
    page += 1;
    offset = 0;
  }
  return items;
}

export type ListsClient = ReturnType<typeof createListsClient>;
export type ListsCollection = RewriteList[] | SchainList[];
